// Modules
import React, { useContext, useEffect, useMemo, useState } from "react";
//@ts-ignore
import { LocalizationContext } from "react-localize";
// Styles
import "./CancelReview.sass";
import { ScreenType } from "../../../../Constants";
import MvLink from "Generic/MvLink";
import moment from "moment";
import { ReservationType } from "Interfaces/GetReservations";
import ToolTip from "Generic/ToolTip";
import MvIcon from "Generic/MvIcon";
import { MvTextOrEdit } from "Generic/MvTextOrEdit";
import { MvTermsConditions } from "Generic/MvTermsConditions";
import { Term } from "Interfaces/CreateWaitlist";
import { MvPrimaryBtn } from "Generic/MvPrimaryBtn";
import { Usage } from "Interfaces/AYPResultInterface";
import { Tip } from "Interfaces/TipInterface";
import { Position } from "Generic/ToolTip/ToolTip";
import CancelConfirmationModal from "../CancelConfirmationModal/CancelConfirmationModal";
import CancelErrorModal from "../CancelErrorModal/CancelErrorModal";
import { logAnalyticData, trackAnalyticData } from "Utils/analytics";
import { EXEC_MODE } from "cfg";
import { ReactAppMode } from "Interfaces/ReactAppModes";
import { OwnerContract, OwnerShipDetails } from "Interfaces/OwnerInterface";
import ContractToolTip from "Interfaces/ContractToolTipInterface";
import ReservationService from "Services/reservation.service";
import MvContractToolTip from "Generic/ToolTip/MvContractToolTip";

type Props = {
  token: string;
  lang: string;
  resId: number;
  resType: ReservationType;
  waitListId?: number;
  setScreenType(screenType: ScreenType): void;
  screenType?: string;
  data: any;
  handleDeleteReservationConfirm: (data: any) => void;
};

const CancelReview = ({
  token,
  lang,
  resId,
  waitListId,
  resType,
  setScreenType,
  screenType,
  data,
  handleDeleteReservationConfirm,
}: Props) => {
  const { localize } = useContext(LocalizationContext);
  const [showUsageTable, setShowUsageTable] = useState(false);
  const [primaryEmail, setPrimaryEmail] = useState("");
  const [additionalEmails, setAdditionalEmails] = useState([""]);
  const [isLoading, setIsLoading] = useState(false);
  const [usageTooltip, setUsageTooltip] = useState<Usage[]>([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [terms, setTerms] = useState(
    (data.terms || []).map((x: any) => ({ ...x, type: x.nodename }))
  );
  const [showTnCError, setShowTnCError] = useState(false);
  const [showEmailError, setShowEmailError] = useState(false);
  const additionalEmail = additionalEmails[0];
  const [showAdditionalEmailError, setShowAdditionalEmailError] = useState(
    false
  );
  const [contractToolTips, setContractToolTips] = useState<ContractToolTip[]>(
    []
  );

  useEffect(() => {
    /**
     * Setting up adobe analytics on click of Cancel
     * This will trigger once the cancel is clicked
     */
    logAnalyticData(
      {
        event: "cancelPoints",
        siteSection: "Points Reservation",
        pageName: "Cancel Points",
        website: "",
      },
      {
        ownerActivity: "Cancel Points",
        cancelStep: "Step 1: Cancel Points",
        errorMessage: "",
        notificationType: "",
      }
    );
  }, []);

  useEffect(() => {
    const email = data.primaryGuest ? data.primaryGuest.email : "-";
    setPrimaryEmail(email);
    const usageData = data.usageDetails ? data.usageDetails.usage || [] : [];
    setUsageTooltip(usageData);
    setTerms((data.terms || []).map((x: any) => ({ ...x, type: x.nodename })));
  }, [data]);

  const usageTableData = useMemo(() => {
    const usageData: any = [];
    const normalUsageTypes =
      (data.cancelReservationPreviewOfUsages &&
        data.cancelReservationPreviewOfUsages.normalUsageTypes) ||
      [];
    if (normalUsageTypes && normalUsageTypes.length > 0) {
      normalUsageTypes.forEach((selectedRow: any) => {
        usageData.push({
          contractNumber: selectedRow.contractNumber,
          contractId: selectedRow.contractId,
          usageType: selectedRow.returnUsageType,
          useBy: `${moment(selectedRow.startDate).format(
            "MMM DD, YYYY"
          )} - ${moment(selectedRow.endDate).format("MMM DD, YYYY")}`,
          pointsReturned: selectedRow.pointsReturned,
          pendingStatus: selectedRow.pendingStatus,
        });
      });
    }
    const holdUsageTypes =
      (data.cancelReservationPreviewOfUsages &&
        data.cancelReservationPreviewOfUsages.holdUsageTypes) ||
      [];
    if (holdUsageTypes && holdUsageTypes.length > 0) {
      holdUsageTypes.forEach((selectedRow: any) => {
        usageData.push({
          contractNumber: selectedRow.contractNumber,
          contractId: selectedRow.contractId,
          usageType: selectedRow.returnUsageType,
          useBy: `${moment(selectedRow.startDate).format(
            "MMM DD, YYYY"
          )} - ${moment(selectedRow.endDate).format("MMM DD, YYYY")}`,
          pointsReturned: selectedRow.pointsReturned,
          pendingStatus: selectedRow.pendingStatus,
        });
      });
    }
    const forefeitUsageTypes =
      (data.cancelReservationPreviewOfUsages &&
        data.cancelReservationPreviewOfUsages.forefeitUsageTypes) ||
      [];
    if (forefeitUsageTypes && forefeitUsageTypes.length > 0) {
      forefeitUsageTypes.forEach((selectedRow: any) => {
        usageData.push({
          contractNumber: selectedRow.contractNumber,
          contractId: selectedRow.contractId,
          usageType: selectedRow.returnUsageType,
          useBy: `${moment(selectedRow.startDate).format(
            "MMM DD, YYYY"
          )} - ${moment(selectedRow.endDate).format("MMM DD, YYYY")}`,
          pointsReturned: selectedRow.pointsReturned,
          pendingStatus: selectedRow.pendingStatus,
        });
      });
    }
    return usageData;
  }, [data]);

  useEffect(() => {
    async function fetchContactTooltipsData() {
      const payload = usageTableData.map((x: any[]) => String((x as any).contractId));
      if (payload.length > 0) {
        var tooltipData = await fetchContactTooltips(payload);
        setContractToolTips(tooltipData);
      }
    }
    fetchContactTooltipsData()
    setContractToolTips([{ contractId: 'a' }as any])
  }, [usageTableData]);

  const validateEmail = (email: string): boolean => {
    const re = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
    return email.trim() === "" || re.test(email);
  };

  const handleBackReservationClick = () => {
    setScreenType(ScreenType.RESERVATIONS);
    window.scrollTo(0, 0);
  };

  const handlePrimaryEmailInputChange = (value: string) => {
    setPrimaryEmail(value);
    const primaryEmailError = !value || !validateEmail(value);
    if (primaryEmailError) {
      setShowEmailError(true);
    } else {
      setShowEmailError(false);
    }
  };

  const handleAdditonalEmailInputChange = (fieldValue: string, index: any) => {
    const updatedEmails = [...additionalEmails];
    updatedEmails[index] = fieldValue;
    setAdditionalEmails(updatedEmails);
    const additionalEmailError = fieldValue && !validateEmail(fieldValue);
    if (additionalEmail && additionalEmailError) {
      setShowAdditionalEmailError(true);
    } else {
      setShowAdditionalEmailError(false);
    }
  };

  const handleAddAdditionalEmail = () => {
    const updatedEmails = [...additionalEmails];
    updatedEmails.push("");
    setAdditionalEmails(updatedEmails);
  };

  const handleRemoveAdditionalEmail = (index: number) => {
    const updatedEmails = [
      ...additionalEmails.filter((email, i) => i !== index),
    ];
    setAdditionalEmails(updatedEmails);
  };

  const convertOCtoCTT = (oc: OwnerContract): ContractToolTip => {
    // the AYP table is still returning mock data? that will be returning real
    //  data this sprint by Rama - done by tomorrow perhaps
    // getting CORS error
    return {
      id: oc.id,
      contractId: String(oc.id),
      contractNumber: oc.contractNumber,
      contractType: oc.contractType,
      // hard code to points for now
      // the service is not providing this field
      // it is also not returning any weeks data
      typeName: "points",
      startDate: oc.contractStartDate,
      endDate: oc.contractEndDate,
      // this field is missing from the service
      // it is being looked into
      // so for now, just show the start date's year
      usageYear: oc.contractStartDate,
      points: oc.contractPoints,
      ownershipDetails: oc.ownershipDetails
        ? oc.ownershipDetails
        : ({} as OwnerShipDetails), //CE2RT1C-899 - 4. include ownership details when constructing contract tooltip
    };
  };

  const getContractToolTipJSX = (contractToolTip: ContractToolTip): any => {
    const contractId = contractToolTip.contractId;
    const key =
      contractId +
      contractToolTip.startDate +
      contractToolTip.endDate +
      (contractToolTip.points ? contractToolTip.points.toString() : "");
    const contractIdStyle = (
      <p
        key={key}
        className="contract-id mv-print-link"
        aria-label={contractId}
        style={{ textDecoration: 'underline' }}
      >
        {contractToolTip.contractNumber}
      </p>
    );

    return (
      <MvContractToolTip contract={contractToolTip} position={Position.RIGHT}>
        {contractIdStyle}
      </MvContractToolTip>
    );
  };

  const fetchContactTooltips = (contractList: any) => {
       // get the contracts' data
       const reservationService = new ReservationService();  
       return reservationService
         .getOwnerContracts(contractList)
         .then((ownerContracts: OwnerContract[]) => {
           // build the ContractToolTip[]
           let contractToolTipsData: ContractToolTip[] = contractToolTips || [];
           ownerContracts.map((contract) => {
             // convert OwnerContract to ContractToolTip
             let tempContractToolTip = convertOCtoCTT(contract);
             // get the tooltip jsx
             tempContractToolTip.toolTip = getContractToolTipJSX(
               tempContractToolTip
             );
             contractToolTipsData.push(tempContractToolTip);
           });
           return contractToolTipsData;
         });
  };

  const getContractToolTip = (contractNumber: string, contractId: any): any => {
    const contractToolTip = contractToolTips.find((ctt: ContractToolTip) => {
      return ctt.contractId == contractId;
    });

    return contractToolTip ? (
      <div style={{ marginTop: "4px" }}>{contractToolTip.toolTip}</div>
    ) : (
      getContractToolTipGeneric(contractId, contractNumber)
    );
  };

  const getContractToolTipGeneric = (contractId: string, contractNumber: string): any => {
    const tip: Tip[] = [
      {
        label: `${localize("static.content.ayp-contract")}`,
        desc: `${contractId}`,
      }
    ];
    const contractIdStyle = (
      <p
        key={contractId}
        className="contract-id mv-print-link"
        aria-label={contractId}
      >
        {contractNumber}
      </p>
    );

    return (
      <div style={{ marginTop: "4px" }}>
        <ToolTip
          useRefToPosition={false}
          id={`contract-generic-${contractId}`}
          tabIndex={-1}
          tip={tip}
          position={Position.RIGHT}
        >
          <u>{contractIdStyle}</u>
        </ToolTip>
      </div>
    );
  };

  const getTerms = () => {
    let termsData = terms;
    if (
      additionalEmails.length == 0 ||
      !additionalEmails[0] ||
      !validateEmail(additionalEmails[0])
    ) {
      termsData = termsData.filter(
        (x: any) => x.type !== "cancel-terms-additional-emails"
      );
    }
    return termsData;
  };

  const handleClickOnTnC = (type: string) => {
    const newTerms = [...terms];

    const term = newTerms.find((term) => term.type === type);

    if (term) {
      term.checked = !term.checked;
    }
    setTerms(newTerms);
  };

  const hasTnCErrors = () => {
    const errorTerms = getTerms().filter((x: any) => !x.checked);
    return errorTerms.length > 0;
  };

  const handleCancelReservationClick = () => {
    trackAnalyticData("cancelReservation");
    const tncError = hasTnCErrors();
    const primaryEmailError = !primaryEmail || !validateEmail(primaryEmail);
    const additionalEmailError =
      additionalEmail && !validateEmail(additionalEmail);
    if (tncError) {
      setShowTnCError(true);
    } else {
      setShowTnCError(false);
    }
    if (primaryEmailError) {
      setShowEmailError(true);
    } else {
      setShowEmailError(false);
    }
    if (additionalEmail && additionalEmailError) {
      setShowAdditionalEmailError(true);
    } else {
      setShowAdditionalEmailError(false);
    }

    if (
      !tncError &&
      !primaryEmailError &&
      (!additionalEmail || (additionalEmail && !additionalEmailError))
    ) {
      handleToggleModal();
    }
  };

  const displayCancelRservationClick = () => {
    return !data.disableVoaCancelCapability;
  };

  const handleToggleModal = () => {
    if (showConfirmation) {
      logAnalyticData(
        {
          event: "cancelPointsAreYouSure",
          siteSection: "Points Reservation",
          pageName: "Cancel Points Are You Sure",
          website: "",
        },
        {
          ownerActivity: "Cancel Points",
          cancelStep: "Step 2: Cancel Points Are You Sure",
          errorMessage: "",
          notificationType: "",
        }
      );
    }
    setShowConfirmation((pre) => !pre);
  };

  const handleErrorToggleModal = (msg?: string) => {
    handleToggleModal();
    setShowError((pre) => !pre);
    setErrorMessage(showError ? "" : msg || "");
  };

  const handleConfirmModal = async () => {
    const payload = {
      ...data,
      primaryGuest: { ...data.primaryGuest, email: primaryEmail },
      additionalEmails: additionalEmails,
    };
    try {
      var res = await handleDeleteReservationConfirm(payload);
      const response = (res as any).cancelReservationResponse;
      if (
        response.status == "error" &&
        response.Error.ErrorCode === "TSW_CANCEL_MARSHA_CANCELATION"
      ) {
        const errorCode = response.Error.ErrorCode;
        let errorMsg = "";
        if (errorCode === "TSW_CANCEL_MARSHA_CANCELATION") {
          errorMsg =
            EXEC_MODE == ReactAppMode.VCAP
              ? `${localize("static.content.tsw-cancellation-error-vcap")}`
              : `${localize("static.content.tsw-cancellation-error-voa")}`;
        } else {
          errorMsg = `${localize("static.content.api-cancellation-error")}`;
        }
        handleErrorToggleModal(errorMsg);
      } else {
        handleToggleModal();
      }
    } catch (e) {
      handleErrorToggleModal(localize("static.content.api-cancellation-error"));
    }
  };

  return (
    <div className="cancel-review-container">
      <CancelConfirmationModal
        key={String(showConfirmation)}
        openModal={showConfirmation}
        toggleModal={handleToggleModal}
        reservationDetails={data.reservationDetails || {}}
        reservationConfirmationNumber={data.reservationNumber}
        onConfirmation={handleConfirmModal}
      />
      <CancelErrorModal
        openModal={showError}
        toggleModal={handleErrorToggleModal}
        errorMessage={errorMessage}
      />
      <p className="summary-title">
        {localize("static.content.confirm-cancellation-header")}
      </p>
      <div className="summary-alert">
        <MvIcon color="#990000" icon="exclamation" width="18px" height="18px" />
        <div>{localize("static.content.cancellation-warning-description")}</div>
      </div>
      <p className="summary-back-to-reservation">
        <MvLink
          label={localize("static.content.back-to-reservations-label")}
          onClick={handleBackReservationClick}
        />
      </p>
      <div className="summary-content">
        <div className="ownership-summary">
          <p className="summary-header">
            {localize("static.content.ownership-usage-summary-header")}
          </p>
          <div className="summary-container">
            {data.cancelReservationPreviewOfUsages &&
              data.cancelReservationPreviewOfUsages.usageSummary &&
              data.cancelReservationPreviewOfUsages.usageSummary.totalPoints >
                0 && (
                <div className="summary-items first">
                  <div className="summary-items-label">
                    {localize("static.content.originally-applied-label")}{" "}
                  </div>
                  <div className="summary-items-value">
                    {
                      data.cancelReservationPreviewOfUsages.usageSummary
                        .totalPoints
                    }{" "}
                    Points
                  </div>
                </div>
              )}
            {data.cancelReservationPreviewOfUsages &&
              data.cancelReservationPreviewOfUsages.usageSummary &&
              data.cancelReservationPreviewOfUsages.usageSummary
                .refundedPointsToOwner > 0 && (
                <div className="summary-items">
                  <div className="summary-items-label">
                    <div>
                      {localize("static.content.refunded-to-you-label")}
                    </div>
                    <ToolTip
                      useRefToPosition={false}
                      tip={[
                        {
                          label: "",
                          desc:
                            "Club Points are returned unrestricted when canceled 61 days or more prior to arrival.",
                        },
                      ]}
                      id="points-needed"
                    >
                      <span className="question-icon">{"?"}</span>
                    </ToolTip>
                  </div>
                  <div className="summary-items-value">
                    {
                      data.cancelReservationPreviewOfUsages.usageSummary
                        .refundedPointsToOwner
                    }{" "}
                    {localize("static.content.points-label")}
                  </div>
                </div>
              )}
            {data.cancelReservationPreviewOfUsages &&
              data.cancelReservationPreviewOfUsages.usageSummary &&
              data.cancelReservationPreviewOfUsages.usageSummary.holdPoints >
                0 && (
                <div className="summary-items">
                  <div className="summary-items-label">
                    <div>
                      {localize("static.content.refunded-to-hold-label")}
                    </div>
                    <ToolTip
                      useRefToPosition={false}
                      tip={[
                        {
                          label: "",
                          desc:
                            "Club Points in a holding account may be used to confirm another reservation within 60 days of arrival.",
                        },
                      ]}
                      id="points-needed"
                    >
                      <span className="question-icon">{"?"}</span>
                    </ToolTip>
                  </div>
                  <div className="summary-items-value">
                    {
                      data.cancelReservationPreviewOfUsages.usageSummary
                        .holdPoints
                    }{" "}
                    Points
                  </div>
                </div>
              )}
            {data.cancelReservationPreviewOfUsages &&
              data.cancelReservationPreviewOfUsages.usageSummary &&
              data.cancelReservationPreviewOfUsages.usageSummary
                .forefeitPoints > 0 && (
                <div className="summary-items">
                  <div className="summary-items-label">
                    <div>{localize("static.content.forfeited-label")}</div>
                    <ToolTip
                      useRefToPosition={false}
                      tip={[
                        {
                          label: "",
                          desc:
                            "Premium Points are forfeited on reservation canceled within 60 days of arrival.",
                        },
                      ]}
                      id="points-needed"
                    >
                      <span className="question-icon">{"?"}</span>
                    </ToolTip>
                  </div>
                  <div className="summary-items-value">
                    -
                    {
                      data.cancelReservationPreviewOfUsages.usageSummary
                        .forefeitPoints
                    }{" "}
                    Points
                  </div>
                </div>
              )}
            <div className="summary-items-footer">
            </div>
          </div>
        </div>
        <div className="reservation-summary">
          <p className="summary-header">
            {localize("static.content.reservation-summary-header")}
          </p>
          <div className="summary-container">
            <p className="summary-body-header">
              {data.reservationDetails
                ? data.reservationDetails.resortName
                : "-"}
            </p>
            <p className="summary-body-subheader">
              {data.reservationDetails
                ? data.reservationDetails.resortLocation
                : "-"}
            </p>
            <div className="summary-body">
              <div>
                <p>
                  <span className="bold-text">
                    {localize("static.content.reservations-reservation-num")}
                  </span>
                  <br />
                  {data.reservationNumber}
                </p>
                <p>
                  <span className="bold-text">
                    {localize("static.content.room-type")}
                  </span>
                  <br />
                  {data.reservationDetails &&
                  data.reservationDetails.roomType ? (
                    <span
                      dangerouslySetInnerHTML={{
                        __html: data.reservationDetails.roomType.description,
                      }}
                    ></span>
                  ) : (
                    "-"
                  )}
                </p>
                <p>
                  <span className="bold-text">
                    {localize("static.content.check-in-check-out-date-label")}
                  </span>
                  <br />
                  {data.reservationDetails &&
                  data.reservationDetails.checkInDate
                    ? moment(data.reservationDetails.checkInDate).format(
                        "MMM DD, YYYY"
                      )
                    : "-"}{" "}
                  -{" "}
                  {data.reservationDetails &&
                  data.reservationDetails.checkOutDate
                    ? moment(data.reservationDetails.checkOutDate).format(
                        "MMM DD, YYYY"
                      )
                    : "-"}
                </p>
              </div>
              <div>
                <p>
                  <span className="bold-text">
                    {localize("static.content.sr-primary-guest")}
                  </span>
                  <br />
                  {data.primaryGuest
                    ? `${data.primaryGuest.firstName} ${data.primaryGuest.lastName}`
                    : "-"}
                </p>
                <p>
                  <span className="bold-text">
                    {localize("static.content.number-of-guests-label")}
                  </span>
                  <br />
                  {data.reservationDetails
                    ? data.reservationDetails.numOfGuests
                    : "-"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
        <div className="summary-usage">
          <p className="summary-usage-header">
            {localize("static.content.usage-summary-table-header")}
          </p>
          <div className="summary-usage-table">
            <table>
              <thead>
                <tr>
                  <th>
                    {localize("static.content.usage-summary-table-contract")}
                  </th>
                  <th>{localize("static.content.summary-table-usage-type")}</th>
                  <th>
                    {localize("static.content.usage-summary-table-use-between")}
                  </th>
                  <th>
                    {localize("static.content.summary-table-points-returned")}
                  </th>
                  <th>
                    {localize("static.content.summary-table-pending-status")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {usageTableData.map((x: any, i: number) => (
                  <tr key={i}>
                    <td>
                      {getContractToolTip(x.contractNumber, x.contractId)}
                    </td>
                    <td>{x.usageType}</td>
                    <td>{x.useBy}</td>
                    <td>{x.pointsReturned}</td>
                    <td>{x.pendingStatus}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {usageTableData.map((x: any, i: number) => (
            <div className="summary-usage-table-tile" key={i}>
              <div className="row">
                <div>
                  <span className="bold-text">Contract</span>
                </div>
                <td>{getContractToolTip(x.contractNumber, x.contractId)}</td>
              </div>
              <div className="row">
                <div>
                  <span className="bold-text">Usage Type</span>
                </div>
                <div
                  // style={{
                  //   width: "75%",
                  //   wordWrap: "break-word",
                  //   textAlign: "right",
                  // }}
                >
                  {x.usageType}
                </div>
              </div>
              <div className="row">
                <div>
                  <span className="bold-text">Use Between</span>
                </div>
                <div>{x.useBy}</div>
              </div>
              <div className="row">
                <div>
                  <span className="bold-text">Points Returned</span>
                </div>
                <div>{x.pointsReturned}</div>
              </div>
              <div className="row">
                <div>
                  <span className="bold-text">Pending Status</span>
                </div>
                <div>{x.pendingStatus}</div>
              </div>
            </div>
          ))}
        </div>
      <div className="cancel-confirmation">
        <div className="cancel-confirmation-header">
          {localize("static.content.confirm-your-cancellation-header")}
        </div>
        <div className="cancel-confirmation-body">
          <div>
            <p className="sub-heading">
              {localize("static.content.email-address-dropdown-label")}
            </p>
            <p>A confirmation email will be sent to this email address.</p>
            <div className="email-address">
              <MvTextOrEdit
                onChange={(val) => handlePrimaryEmailInputChange(val)}
                onBlur={(e) => {}}
                value={primaryEmail}
                placeholder={""}
                isEditing={true}
                hideLabel={true}
                label={"primaryEmail"}
                id={"currentEmail"}
                name={"currentEmail"}
                hasError={showEmailError}
                errorMessage={
                  primaryEmail
                    ? "Primary Email is Invalid"
                    : localize("static.content.invalid-email-error-message")
                }
                isRequired={true}
                inputComponent="text"
              />
              <div className={`removeItem`} style={{ width: "10px" }}></div>
            </div>
          </div>
          <div>
            <p className="sub-heading-additional">
              {localize("static.content.additional-email-address-label")}
            </p>
            <p className="sub-heading-additional-note">
              To notify other parties of this reservation cancelation, add their
              emails below.
            </p>
            {additionalEmails.map((x: any, i: any) => (
              <div key={i} className="additional-email-address">
                <MvTextOrEdit
                  onChange={(val) => handleAdditonalEmailInputChange(val, i)}
                  onBlur={(e) => {}}
                  value={x}
                  placeholder={""}
                  isEditing={true}
                  hideLabel={true}
                  label={""}
                  id={`additionalEmail_${i}`}
                  name={`additionalEmail_${i}`}
                  hasError={showAdditionalEmailError}
                  errorMessage={"Incorrect Additional Email"}
                  isRequired={false}
                  inputComponent="text"
                />
                <div
                  className={`removeItem`}
                  /* Commenting out this piece of code as per bussiness requirement */
                  // onClick={() => handleRemoveAdditionalEmail(i)}
                >
                  {/* <MvIcon color="#009687" height="14px" width="14px" icon="x" /> */}
                </div>
              </div>
            ))}
            {/* {additionalEmails.length < 1 && (
              <p
                className="add-email-address"
                onClick={handleAddAdditionalEmail}
              >
                + Add Email Address
              </p>
            )} */}
          </div>
        </div>
        <div className="cancel-toc">
          <MvTermsConditions
            heading={localize("static.content.sr-tnc-heading")}
            subHeading={localize("static.content.sr-tnc-sub-heading")}
            instructions={getTerms()}
            onChange={handleClickOnTnC}
            submissionFailed={showTnCError}
            readOnly={false}
          />
        </div>
        <p className="cancel-reservation-btn">
            <MvPrimaryBtn
              isDisabled={!displayCancelRservationClick()}
              submissionInProgress={false}
              text={localize("static.content.cancel-reservation-cta-label")}
              onClick={handleCancelReservationClick}
            />
        </p>
        <p className="keep-my-reservation">
          <MvLink
            label={localize("static.content.keep-my-reservation-cta-label")}
            onClick={handleBackReservationClick}
          />
        </p>
      </div>
    </div>
  );
};

export { CancelReview };
